import { UnitInput } from "@/components/primitives/unit-input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { calculateDairyComposition } from "@/lib/ingredients/form-estimators";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { CreateIngredientFormData } from "./schema";
import { SimpleFormWrapper } from "./simple-wrapper";
export { calculateDairyComposition } from "@/lib/ingredients/form-estimators";

type SimpleFormProps = {
  form: UseFormReturn<CreateIngredientFormData>;
};

type DairyComposition = Pick<
  CreateIngredientFormData,
  | "fatPct"
  | "waterPct"
  | "proteinPct"
  | "carbPct"
  | "fiberPct"
  | "alcoholPct"
  | "sugarsPct"
  | "lactosePct"
  | "saturatedFatPct"
  | "energyKcal"
  | "pod"
  | "pac"
>;

const DAIRY_DERIVED_FIELDS = [
  "waterPct",
  "proteinPct",
  "carbPct",
  "fiberPct",
  "alcoholPct",
  "sugarsPct",
  "lactosePct",
  "saturatedFatPct",
  "energyKcal",
  "pod",
  "pac",
] as const satisfies ReadonlyArray<keyof DairyComposition>;

// Values are taken from a simple linear regression model taken from
// values of skim milk, whole milk, cream, and double cream.
export function SimpleDairyForm({ form }: SimpleFormProps) {
  const fatPctValue = form.watch("fatPct");

  useEffect(() => {
    const calculated = calculateDairyComposition(fatPctValue);
    for (const field of DAIRY_DERIVED_FIELDS) {
      form.setValue(field, calculated[field]);
    }
  }, [fatPctValue, form]);

  return (
    <SimpleFormWrapper form={form} selectedCategory="dairy">
      <FormField
        control={form.control}
        name="fatPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Milk Fat %</FormLabel>
            <FormDescription>
              This should match the total fat content. Typical values are milk:
              0-5%, half & half: 10-18%, cream: 30-50%, double cream: 40-50%.
            </FormDescription>
            <FormControl>
              <UnitInput {...field} unit="%" min={0} max={100} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </SimpleFormWrapper>
  );
}
